import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["column", "card", "count", "empty"]

  connect() {
    this.draggedCard = null
    this.originalColumn = null
    this.originalNextSibling = null
    this.originalStatus = null
  }

  dragStart(event) {
    if (event.target.closest("a, button, input, select, textarea, label")) {
      event.preventDefault()
      return
    }

    const card = event.currentTarget
    this.draggedCard = card
    this.originalColumn = card.closest("[data-lead-pipeline-target='column']")
    this.originalNextSibling = card.nextElementSibling
    this.originalStatus = card.dataset.status

    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", card.dataset.leadId)

    requestAnimationFrame(() => card.classList.add("is-dragging"))
  }

  dragEnd(event) {
    event.currentTarget.classList.remove("is-dragging")
    this.clearDropState()
    this.draggedCard = null
    this.originalColumn = null
    this.originalNextSibling = null
    this.originalStatus = null
  }

  dragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    event.currentTarget.classList.add("is-drag-over")
  }

  dragLeave(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return

    event.currentTarget.classList.remove("is-drag-over")
  }

  async drop(event) {
    event.preventDefault()

    const column = event.currentTarget
    const status = column.dataset.status
    const card = this.draggedCard || this.findCard(event.dataTransfer.getData("text/plain"))

    this.clearDropState()

    if (!card || !status || card.dataset.status === status) return

    const previousColumn = card.closest("[data-lead-pipeline-target='column']")
    const previousNextSibling = card.nextElementSibling
    const previousStatus = card.dataset.status

    this.moveCardToColumn(card, column)
    this.setCardStatus(card, status)
    this.updateVisibleCounts()
    card.classList.add("is-updating")

    try {
      const payload = await this.persistStatus(card, status)
      this.setCardStatus(card, payload.lead.status)
      this.updateTotalCounts(payload.counts)
      this.updateVisibleCounts()
    } catch (error) {
      this.restoreCard(card, previousColumn, previousNextSibling)
      this.setCardStatus(card, previousStatus)
      this.updateVisibleCounts()
      window.alert(error.message || "Não foi possível mover o lead agora.")
    } finally {
      card.classList.remove("is-updating")
    }
  }

  moveCardToColumn(card, column) {
    const empty = column.querySelector("[data-lead-pipeline-target='empty']")

    column.insertBefore(card, empty || null)
  }

  restoreCard(card, column, nextSibling) {
    if (!column) return

    column.insertBefore(card, nextSibling || column.querySelector("[data-lead-pipeline-target='empty']") || null)
  }

  setCardStatus(card, status) {
    card.dataset.status = status

    const select = card.querySelector("select[name='lead[status]']")
    if (select) select.value = status
  }

  async persistStatus(card, status) {
    const response = await fetch(card.dataset.updateUrl, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ lead: { status } })
    })

    const payload = await response.json().catch(() => ({}))

    if (!response.ok || payload.ok === false) {
      throw new Error((payload.errors || ["Não foi possível mover o lead agora."]).join(" "))
    }

    return payload
  }

  updateTotalCounts(counts) {
    if (!counts) return

    this.countElements
      .filter((count) => count.dataset.countKind === "total")
      .forEach((count) => {
        const value = counts[count.dataset.status]
        if (value !== undefined) count.textContent = value
      })
  }

  updateVisibleCounts() {
    this.columnTargets.forEach((column) => {
      const status = column.dataset.status
      const visibleCount = column.querySelectorAll("[data-lead-pipeline-target='card']").length

      this.countElements
        .filter((count) => count.dataset.countKind === "visible" && count.dataset.status === status)
        .forEach((count) => {
          count.textContent = `${visibleCount} nesta visualização`
        })

      const empty = column.querySelector("[data-lead-pipeline-target='empty']")
      if (empty) empty.hidden = visibleCount > 0
    })
  }

  clearDropState() {
    this.columnTargets.forEach((column) => column.classList.remove("is-drag-over"))
  }

  findCard(leadId) {
    return this.cardTargets.find((card) => card.dataset.leadId === leadId)
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  get countElements() {
    return Array.from(document.querySelectorAll("[data-lead-pipeline-target='count']"))
  }
}
